import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  ContractCrewContractOption,
  ContractCrewItem,
  ContractCrewsResponse,
  UpsertContractCrewRequest,
  createContractCrew,
  deleteContractCrew,
  getContractCrews,
  updateContractCrew,
} from "../../../services/ContractCrewsService";
import UserContractSelect from "../UserContractSelect";

interface ContractCrewsPageProps {
  onBack?: () => void;
  initialData?: ContractCrewsResponse | null;
  onDataLoaded?: (data: ContractCrewsResponse) => void;
}

type FormState = UpsertContractCrewRequest;

const emptyForm: FormState = {
  name: "",
  description: "",
};

const isForbiddenError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 403;

export default function ContractCrewsPage({
  onBack: _onBack,
  initialData,
  onDataLoaded,
}: Readonly<ContractCrewsPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 512,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();
  const canManage = checkAccess("ADMIN_PRICES");

  const [crews, setCrews] = useState<ContractCrewItem[]>(
    initialData?.crews ?? [],
  );
  const [availableContracts, setAvailableContracts] = useState<
    ContractCrewContractOption[]
  >(initialData?.availableContracts ?? []);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    initialData?.selectedContractId ?? null,
  );
  const [crewsLoading, setCrewsLoading] = useState(false);
  const loadSeqRef = useRef(0);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingCrewId, setEditingCrewId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  const showError = useCallback(
    (fallback: string, error: unknown) => {
      console.error(error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : fallback);
      notifications.show({
        title: t("REPORTS_ERROR", "Error"),
        message,
        color: "red",
      });
    },
    [t],
  );

  const publishData = useCallback(
    (
      contractId: number | null,
      nextCrews: ContractCrewItem[],
      nextAvailableContracts: ContractCrewContractOption[] = availableContracts,
    ) => {
      onDataLoaded?.({
        availableContracts: nextAvailableContracts,
        selectedContractId: contractId,
        crews: nextCrews,
      });
    },
    [availableContracts, onDataLoaded],
  );

  useEffect(() => {
    if (!canManage) {
      setAccessDenied(true);
      return;
    }
    setAccessDenied(false);
  }, [canManage]);

  useEffect(() => {
    if (!initialData) return;
    setSelectedContractId(initialData.selectedContractId ?? null);
    setCrews(initialData.crews);
    setAvailableContracts(initialData.availableContracts);
  }, [initialData]);

  const loadCrewsForContract = useCallback(
    async (contractId: number) => {
      const seq = ++loadSeqRef.current;
      setCrewsLoading(true);
      try {
        const result = await getContractCrews(selectedAddressSystemId, contractId);
        if (seq !== loadSeqRef.current) return;

        setCrews(result.crews);
        setAvailableContracts(result.availableContracts);
        publishData(contractId, result.crews, result.availableContracts);
      } catch (error) {
        if (seq !== loadSeqRef.current) return;

        if (isForbiddenError(error)) {
          setAccessDenied(true);
        } else {
          showError("Failed to load working groups for contract", error);
        }
      } finally {
        if (seq === loadSeqRef.current) {
          setCrewsLoading(false);
        }
      }
    },
    [publishData, selectedAddressSystemId, showError],
  );

  const handleContractChange = (contractId: number | null) => {
    loadSeqRef.current += 1;
    setSelectedContractId(contractId);
    if (contractId) {
      void loadCrewsForContract(contractId);
      return;
    }

    setCrewsLoading(false);
    setCrews([]);
    publishData(null, []);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setModalOpened(false);
    setEditingCrewId(null);
    setForm(emptyForm);
    setNameError(null);
  };

  const openCreate = () => {
    setEditingCrewId(null);
    setForm(emptyForm);
    setNameError(null);
    setModalOpened(true);
  };

  const openEdit = (crew: ContractCrewItem) => {
    setEditingCrewId(crew.id);
    setForm({ name: crew.name, description: crew.description ?? "" });
    setNameError(null);
    setModalOpened(true);
  };

  const buildRequest = (): UpsertContractCrewRequest => ({
    name: form.name.trim(),
    description: form.description?.trim() || null,
  });

  const handleSave = async () => {
    if (!form.name.trim()) {
      setNameError(t("CREWS_NAME_REQUIRED", "Name is required"));
      return;
    }
    if (!selectedContractId) return;

    setSaving(true);
    try {
      const request = buildRequest();
      const currentEditingId = editingCrewId;

      if (currentEditingId) {
        await updateContractCrew(
          selectedAddressSystemId,
          selectedContractId,
          currentEditingId,
          request,
        );
        setCrews((prev) => {
          const nextCrews = prev.map((c) =>
            c.id === currentEditingId
              ? { ...c, name: request.name, description: request.description ?? null }
              : c,
          );
          publishData(selectedContractId, nextCrews);
          return nextCrews;
        });
        notifications.show({
          title: t("CREWS_SUCCESS", "Success"),
          message: t("CREWS_UPDATED", "Working group updated"),
          color: "green",
        });
      } else {
        const newId = await createContractCrew(
          selectedAddressSystemId,
          selectedContractId,
          request,
        );
        const newCrew: ContractCrewItem = {
          id: newId,
          name: request.name,
          description: request.description ?? null,
          isInUse: false,
        };
        setCrews((prev) => {
          const updatedCrews = [...prev, newCrew].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          publishData(selectedContractId, updatedCrews);
          return updatedCrews;
        });
        notifications.show({
          title: t("CREWS_SUCCESS", "Success"),
          message: t("CREWS_CREATED", "Working group created"),
          color: "green",
        });
      }

      closeModal(true);
    } catch (error) {
      showError(
        editingCrewId ? "Failed to update working group" : "Failed to create working group",
        error,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (crew: ContractCrewItem) => {
    if (!selectedContractId) return;
    try {
      await deleteContractCrew(selectedAddressSystemId, selectedContractId, crew.id);
      setCrews((prev) => {
        const nextCrews = prev.filter((c) => c.id !== crew.id);
        publishData(selectedContractId, nextCrews);
        return nextCrews;
      });
      notifications.show({
        title: t("CREWS_SUCCESS", "Success"),
        message: t("CREWS_DELETED", "Working group deleted"),
        color: "green",
      });
    } catch (error) {
      showError("Failed to delete working group", error);
    }
  };

  const confirmDelete = (crew: ContractCrewItem) => {
    modals.openConfirmModal({
      title: t("CREWS_DELETE_TITLE", "Delete working group"),
      children: (
        <Text size="sm">
          {t("CREWS_DELETE_CONFIRMATION", `Delete working group "${crew.name}"?`)}
        </Text>
      ),
      labels: {
        confirm: t("COMMON_DELETE", "Delete"),
        cancel: t("COMMON_CANCEL", "Cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void handleDelete(crew);
      },
    });
  };

  const memberCount = (description?: string | null) => {
    if (!description) return 0;
    return description
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0).length;
  };

  return (
    <Stack h="100%" gap="md">
      <Group justify="space-between">
        <Text fw={700}>{t("TEXT_LINK_CREWS", "Working groups (crews)")}</Text>
        {canManage && !accessDenied && selectedContractId && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            {t("CREWS_ADD", "Add working group")}
          </Button>
        )}
      </Group>

      {accessDenied ? (
        <Text c="dimmed" size="sm">
          {t(
            "SETTINGS_NO_LINKS_FOR_ROLE",
            "No settings links match your permissions.",
          )}
        </Text>
      ) : (
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          <UserContractSelect
            contracts={availableContracts}
            label={t("CREWS_SELECT_CONTRACT", "Contract")}
            placeholder={t(
              "CREWS_SELECT_CONTRACT_PLACEHOLDER",
              "Select a contract",
            )}
            emptyHint={t(
              "CREWS_NO_CONTRACTS",
              "No contracts available for this address system.",
            )}
            value={selectedContractId}
            onChange={handleContractChange}
          />

          {!selectedContractId ? (
            <Text c="dimmed" size="sm">
              {t(
                "CREWS_SELECT_CONTRACT_FIRST",
                "Select a contract to manage its working groups.",
              )}
            </Text>
          ) : crewsLoading ? (
            <Group justify="center" style={{ flex: 1 }}>
              <Loader size="sm" />
            </Group>
          ) : crews.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t(
                "CREWS_EMPTY",
                "No working groups found. Add one to get started.",
              )}
            </Text>
          ) : (
            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "var(--mantine-radius-sm)",
              }}
            >
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("CREWS_COL_NAME", "Name")}</Table.Th>
                    <Table.Th>{t("CREWS_COL_MEMBERS", "Members")}</Table.Th>
                    {canManage && (
                      <Table.Th style={{ width: 96 }}>
                        {t("CREWS_COL_ACTIONS", "Actions")}
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {crews.map((crew) => (
                    <Table.Tr key={crew.id}>
                      <Table.Td>{crew.name}</Table.Td>
                      <Table.Td>
                        {memberCount(crew.description) > 0
                          ? `${memberCount(crew.description)} ${t("CREWS_MEMBERS_COUNT", "member(s)")}`
                          : "-"}
                      </Table.Td>
                      {canManage && (
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              aria-label={t("COMMON_EDIT", "Edit")}
                              onClick={() => openEdit(crew)}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t("COMMON_DELETE", "Delete")}
                              onClick={() => confirmDelete(crew)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={() => closeModal()}
        title={
          editingCrewId
            ? t("CREWS_EDIT_TITLE", "Edit working group")
            : t("CREWS_ADD_TITLE", "Add working group")
        }
      >
        <Stack>
          <TextInput
            label={t("CREWS_NAME", "Name")}
            value={form.name}
            error={nameError}
            required
            onChange={(event) => {
              setForm((f) => ({ ...f, name: event.currentTarget.value }));
              setNameError(null);
            }}
          />
          <Textarea
            label={t("CREWS_DESCRIPTION", "Members (one per line)")}
            description={t(
              "CREWS_DESCRIPTION_HINT",
              "Enter one member name per line",
            )}
            value={form.description ?? ""}
            onChange={(event) =>
              setForm((f) => ({
                ...f,
                description: event.currentTarget.value,
              }))
            }
            minRows={4}
            autosize
            maxRows={12}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => closeModal()}
              disabled={saving}
            >
              {t("COMMON_CANCEL", "Cancel")}
            </Button>
            <Button onClick={() => void handleSave()} loading={saving}>
              {t("COMMON_SAVE", "Save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
